import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** Account security: credential change, device PIN and active sessions. */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { KeyRound, Smartphone, ShieldCheck, Monitor, LogOut } from 'lucide-react';
import { resolveBackendBase } from '../utils/backendBase.js';
import { getStoredToken, refreshAccessToken, storeTokens } from '../utils/authToken.js';
import { evaluatePassword, passwordsMatch } from '../utils/passwordPolicy.js';
import { evaluatePin, normalizePin, PIN_LENGTH } from '../utils/pinPolicy.js';

const TRUSTED_DEVICE_KEY = 'trusted_device';

async function currentBearer() {
  return getStoredToken() || await refreshAccessToken();
}

async function authRequest(path, { method = 'POST', body } = {}) {
  try {
    const token = await currentBearer();
    const headers = {};
    if (body !== undefined)
      headers['Content-Type'] = 'application/json';
    if (token)
      headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${resolveBackendBase()}/api${path}`, {
      method,
      headers,
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const data = await response.json().catch(() => ({}));
    if (response.ok && data?.access_token)
      storeTokens(data);
    return { ok: response.ok, status: response.status, data };
  }
  catch {
    return { ok: false, status: 0, data: { detail: 'تعذر الاتصال بالخادم' } };
  }
}

function storedSession() {
  try {
    return JSON.parse(localStorage.getItem('session') || 'null') || {};
  }
  catch {
    return {};
  }
}

export const SecuritySettings = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [pin, setPin] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [savingPin, setSavingPin] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsBusy, setSessionsBusy] = useState(false);
  const session = storedSession();
  const currentUsername = session.username || '';
  const currentUserId = session.id || '';
  const pinSavedInBrowser = (() => {
    try {
      const device = JSON.parse(localStorage.getItem(TRUSTED_DEVICE_KEY) || 'null');
      return Boolean(device?.device_id && (
        (currentUserId && String(device?.user_id || '') === String(currentUserId)) ||
        (!currentUserId && device?.username === currentUsername)
      ));
    }
    catch {
      return false;
    }
  })();
  const passwordPolicy = evaluatePassword(password);
  const passwordMatches = passwordsMatch(password, password2);
  const pinPolicy = evaluatePin(pin);

  const loadSessions = async () => {
    const result = await authRequest('/auth/sessions', { method: 'GET' });
    if (result.ok)
      setSessions(Array.isArray(result.data?.data) ? result.data.data : []);
  };
  useEffect(() => {
    loadSessions();
  }, []);

  const requireCurrentPassword = () => {
    if (currentPassword)
      return true;
    toast.error('أدخل كلمة المرور الحالية لتأكيد هذا التغيير');
    return false;
  };
  const savePassword = async () => {
    if (!requireCurrentPassword())
      return;
    if (!passwordPolicy.eligible) {
      toast.error('استخدم 12 حرفاً على الأقل وثلاثة أنواع من الأحرف');
      return;
    }
    if (!passwordMatches) {
      toast.error('كلمتا المرور غير متطابقتين');
      return;
    }
    setSavingPw(true);
    try {
      const result = await authRequest('/auth/set-password', {
        body: { current_password: currentPassword, new_password: password },
      });
      if (result.ok) {
        localStorage.removeItem(TRUSTED_DEVICE_KEY);
        toast.success('تم تغيير كلمة المرور وإنهاء الجلسات الأخرى');
        setCurrentPassword('');
        setPassword('');
        setPassword2('');
        loadSessions();
      }
      else {
        toast.error(result.data?.detail || 'فشل تغيير كلمة المرور');
      }
    }
    finally {
      setSavingPw(false);
    }
  };
  const savePin = async () => {
    if (!requireCurrentPassword())
      return;
    if (!currentUsername || !currentUserId) {
      toast.error('تعذر تأكيد هوية الحساب؛ سجّل الدخول مجدداً');
      return;
    }
    if (!pinPolicy.eligible) {
      toast.error(pinPolicy.message || 'اختر PIN آمناً من 6 أرقام');
      return;
    }
    setSavingPin(true);
    try {
      const result = await authRequest('/auth/set-pin', {
        body: { current_password: currentPassword, pin: pinPolicy.normalized },
      });
      if (result.ok && result.data?.device_id) {
        localStorage.setItem(TRUSTED_DEVICE_KEY, JSON.stringify({
          device_id: result.data.device_id,
          username: currentUsername,
          user_id: currentUserId,
        }));
        toast.success('تم تفعيل PIN لهذا المتصفح وإبطال الأجهزة الموثوقة السابقة');
        setCurrentPassword('');
        setPin('');
      }
      else {
        toast.error(result.data?.detail || 'فشل تعيين PIN');
      }
    }
    finally {
      setSavingPin(false);
    }
  };
  const revokeSession = async (sessionId) => {
    setSessionsBusy(true);
    try {
      const result = await authRequest('/auth/sessions/revoke', { body: { session_id: sessionId } });
      if (result.ok) {
        toast.success('تم إنهاء الجلسة');
        loadSessions();
      }
      else {
        toast.error(result.data?.detail || 'تعذر إنهاء الجلسة');
      }
    }
    finally {
      setSessionsBusy(false);
    }
  };
  const revokeOthers = async () => {
    setSessionsBusy(true);
    try {
      const result = await authRequest('/auth/sessions/revoke-others');
      if (result.ok) {
        toast.success('تم إنهاء الجلسات الأخرى');
        loadSessions();
      }
      else {
        toast.error(result.data?.detail || 'تعذر إنهاء الجلسات الأخرى');
      }
    }
    finally {
      setSessionsBusy(false);
    }
  };
  const cardStyle = { backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' };
  const textStyle = { color: 'var(--text-primary)' };
  const mutedStyle = { color: 'var(--text-secondary)' };
  return _jsxs("div", { className: "rounded-2xl border p-4 space-y-6", "data-testid": "security-settings", style: cardStyle, children: [
    _jsxs("h2", { className: "text-lg font-bold flex items-center gap-2", style: textStyle, children: [_jsx(ShieldCheck, { size: 18 }), "أمان الحساب"] }),
    _jsxs("div", { className: "space-y-3", children: [
      _jsxs("p", { className: "text-xs leading-6", style: mutedStyle, children: ["تحتاج تغييرات كلمة المرور وPIN إلى تأكيد كلمة المرور الحالية. لا نعرضها أو نخزنها في المتصفح."] }),
      _jsx("input", { type: "password", autoComplete: "current-password", placeholder: "كلمة المرور الحالية", value: currentPassword, onChange: event => setCurrentPassword(event.target.value), className: "apple-input", "data-testid": "security-current-password-input" }),
    ] }),
    _jsxs("div", { className: "space-y-3 border-t pt-4", style: { borderColor: 'var(--border-color)' }, children: [
      _jsxs("h3", { className: "text-sm font-semibold flex items-center gap-2", style: textStyle, children: [_jsx(KeyRound, { size: 15 }), "تغيير كلمة المرور"] }),
      _jsxs("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-3", children: [
        _jsx("input", { type: "password", autoComplete: "new-password", placeholder: "كلمة المرور الجديدة", value: password, onChange: event => setPassword(event.target.value), className: "apple-input", "data-testid": "security-new-password-input" }),
        _jsx("input", { type: "password", autoComplete: "new-password", placeholder: "تأكيد كلمة المرور", value: password2, onChange: event => setPassword2(event.target.value), className: "apple-input", "data-testid": "security-confirm-password-input" }),
      ] }),
      _jsx("p", { className: "text-xs", style: { color: passwordPolicy.eligible ? '#16a34a' : 'var(--text-secondary)' }, "data-testid": "security-password-strength", children: `${passwordPolicy.strength} — 12 حرفاً على الأقل وثلاثة أنواع من الأحرف` }),
      password2 && _jsx("p", { className: "text-xs", style: { color: passwordMatches ? '#16a34a' : '#dc2626' }, children: passwordMatches ? '✓ كلمتا المرور متطابقتان' : 'كلمتا المرور غير متطابقتين' }),
      _jsx("button", { onClick: savePassword, disabled: savingPw || !passwordPolicy.eligible || !passwordMatches, className: "apple-button !w-auto px-6 disabled:opacity-50", "data-testid": "security-save-password-button", children: savingPw ? 'جارٍ الحفظ…' : 'حفظ كلمة المرور' }),
    ] }),
    _jsxs("div", { className: "space-y-3 border-t pt-4", style: { borderColor: 'var(--border-color)' }, children: [
      _jsxs("h3", { className: "text-sm font-semibold flex items-center gap-2", style: textStyle, children: [_jsx(Smartphone, { size: 15 }), "الدخول السريع بـ PIN", pinSavedInBrowser && _jsx("span", { className: "text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700", "data-testid": "security-pin-saved-badge", children: "مُعَدّ في هذا المتصفح" })] }),
      _jsx("p", { className: "text-xs leading-6", style: mutedStyle, children: "يُقبل PIN على جهاز موثوق فقط. تغيير الرمز يُبطل الأجهزة الموثوقة السابقة." }),
      _jsxs("div", { className: "flex flex-col sm:flex-row sm:items-center gap-3", children: [
        _jsx("input", { type: "password", inputMode: "numeric", autoComplete: "off", maxLength: PIN_LENGTH, placeholder: "PIN (6 أرقام)", value: pin, onChange: event => setPin(normalizePin(event.target.value).slice(0, PIN_LENGTH)), className: "apple-input !w-full sm:!w-44 text-center tracking-[0.4em]", "data-testid": "security-pin-input" }),
        _jsx("button", { onClick: savePin, disabled: savingPin || !pinPolicy.eligible, className: "apple-button !w-auto px-6 disabled:opacity-50", "data-testid": "security-save-pin-button", children: savingPin ? 'جارٍ الحفظ…' : 'تفعيل PIN' }),
      ] }),
      pin && !pinPolicy.eligible && _jsx("p", { className: "text-xs", style: { color: '#dc2626' }, children: pinPolicy.message }),
    ] }),
    _jsxs("div", { className: "space-y-3 border-t pt-4", style: { borderColor: 'var(--border-color)' }, children: [
      _jsxs("div", { className: "flex flex-wrap items-center justify-between gap-3", children: [
        _jsxs("h3", { className: "text-sm font-semibold flex items-center gap-2", style: textStyle, children: [_jsx(Monitor, { size: 15 }), "الجلسات النشطة"] }),
        _jsx("button", { type: "button", onClick: revokeOthers, disabled: sessionsBusy || !sessions.some(item => !item.current), className: "apple-button-secondary !w-auto px-4 disabled:opacity-50", "data-testid": "security-revoke-other-sessions", children: "إنهاء الجلسات الأخرى" }),
      ] }),
      _jsx("p", { className: "text-xs leading-6", style: mutedStyle, children: "عند إنهاء جلسة، لا يعود رمز الوصول الخاص بها صالحاً فوراً." }),
      _jsx("div", { className: "space-y-2", "data-testid": "security-sessions-list", children: sessions.length ? sessions.map(item => _jsxs("div", { className: "flex items-center justify-between gap-3 rounded-xl border p-3 text-xs", style: { borderColor: 'var(--border-color)' }, children: [
        _jsxs("div", { children: [_jsx("strong", { style: textStyle, children: item.current ? 'هذا الجهاز' : 'جلسة أخرى' }), _jsx("p", { style: mutedStyle, children: item.issued_at ? `بدأت: ${new Date(item.issued_at).toLocaleString('ar-SA')}` : '' })] }),
        !item.current && _jsx("button", { type: "button", onClick: () => revokeSession(item.session_id), disabled: sessionsBusy, className: "text-red-600 hover:text-red-700 disabled:opacity-50", "data-testid": `security-revoke-session-${item.session_id}`, children: _jsx(LogOut, { size: 16 }) }),
      ] }, item.session_id)) : _jsx("p", { className: "text-xs", style: mutedStyle, children: "لا توجد جلسات أخرى ظاهرة." }) }),
    ] }),
  ] });
};
export default SecuritySettings;
